import {
  BatteryFull as BatteryFullIcon,
  BatteryAlert as BatteryAlertIcon,
  Battery20 as Battery20Icon,
  Battery50 as Battery50Icon,
  Battery80 as Battery80Icon,
  Info as InfoIcon,
  RvHookup as TrailersIcon,
  PowerOff as UnplugIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { AnyTrackLocation } from "../../../../api/anyTrackApi";
import { colors } from "../../../../theme/colors";

interface TrailerListProps {
  trailers: AnyTrackLocation[];
  selectedTrailerId: string | null;
  onTrailerSelect: (deviceId: string) => void;
  isLoading?: boolean;
  isAnyTrackEnabled?: boolean;
  sentinelRef: React.Ref<HTMLLIElement>;
  listRef: React.Ref<HTMLUListElement>;
  isLoadingMore?: boolean;
  hasMore?: boolean;
}

const TRAILER_ACCENT = "#facc15";
const TRAILER_SKELETON_COUNT = 8;

const DRIVING_STATUS_CONFIG = {
  0: { label: "Idle", color: "#9E9E9E" },
  1: { label: "Driving", color: "#4CAF50" },
  2: { label: "ACC Off", color: "#455A64" },
};

const BatteryIcon: React.FC<{ level: number | null }> = ({ level }) => {
  if (level === null) return null;

  const iconProps = { sx: { fontSize: "0.85rem" } };

  if (level <= 15)
    return <BatteryAlertIcon {...iconProps} sx={{ ...iconProps.sx, color: "error.main" }} />;
  if (level <= 30)
    return <Battery20Icon {...iconProps} sx={{ ...iconProps.sx, color: "warning.main" }} />;
  if (level <= 60)
    return <Battery50Icon {...iconProps} sx={{ ...iconProps.sx, color: "text.secondary" }} />;
  if (level <= 85)
    return <Battery80Icon {...iconProps} sx={{ ...iconProps.sx, color: "success.main" }} />;
  return <BatteryFullIcon {...iconProps} sx={{ ...iconProps.sx, color: "success.main" }} />;
};

const DEVICE_STATUS_CONFIG = {
  Unplugged: { label: "Unplugged", color: "#f44336", icon: true },
  Removed: { label: "Removed", color: "#9E9E9E", icon: false },
  Offline: { label: "Offline", color: "#757575", icon: false },
} as const;

const TrailerRow: React.FC<{
  trailer: AnyTrackLocation;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ trailer, isSelected, onSelect }) => {
  const theme = useTheme();
  const isUnplugged = trailer.deviceStatus === "Unplugged";

  const statusKey = trailer.drivingStatus as 0 | 1 | 2 | null;
  const statusConfig =
    statusKey !== null && statusKey in DRIVING_STATUS_CONFIG
      ? DRIVING_STATUS_CONFIG[statusKey as keyof typeof DRIVING_STATUS_CONFIG]
      : null;

  const deviceStatusConfig =
    trailer.deviceStatus && trailer.deviceStatus in DEVICE_STATUS_CONFIG
      ? DEVICE_STATUS_CONFIG[trailer.deviceStatus as keyof typeof DEVICE_STATUS_CONFIG]
      : null;

  const displayName = trailer.vehicleName || trailer.deviceId.slice(-8);

  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={onSelect}
        sx={{
          py: 0.75,
          opacity: trailer.isRemoved ? 0.5 : 1,
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.light.background.selected
                : colors.dark.background.selected,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? colors.light.background.selectedHover
                  : colors.dark.background.selectedHover,
            },
          },
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.25 }}
        >
          {/* Name row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isUnplugged ? (
              <Tooltip title="Device unplugged!">
                <UnplugIcon
                  sx={{ fontSize: "0.95rem", color: "#f44336", flexShrink: 0 }}
                />
              </Tooltip>
            ) : (
              <TrailersIcon
                sx={{ fontSize: "0.95rem", color: TRAILER_ACCENT, flexShrink: 0 }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color:
                  theme.palette.mode === "light"
                    ? colors.light.text.primary
                    : colors.dark.text.primary,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
              title={displayName}
            >
              {displayName}
            </Typography>
          </Box>

          {/* Plate row */}
          {trailer.licensePlateNumber && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7rem",
                color:
                  theme.palette.mode === "light"
                    ? colors.light.text.secondary
                    : colors.dark.text.secondary,
              }}
            >
              {trailer.licensePlateNumber}
            </Typography>
          )}

          {/* Status + speed + battery row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {deviceStatusConfig ? (
              <Box
                sx={{
                  px: 0.75,
                  py: 0.125,
                  borderRadius: 0.5,
                  bgcolor: `${deviceStatusConfig.color}25`,
                  border: `1px solid ${deviceStatusConfig.color}50`,
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  color: deviceStatusConfig.color,
                  lineHeight: 1.4,
                }}
              >
                {deviceStatusConfig.label}
              </Box>
            ) : statusConfig ? (
              <Box
                sx={{
                  px: 0.75,
                  py: 0.125,
                  borderRadius: 0.5,
                  bgcolor: `${statusConfig.color}25`,
                  border: `1px solid ${statusConfig.color}50`,
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  color: statusConfig.color,
                  lineHeight: 1.4,
                }}
              >
                {statusConfig.label}
              </Box>
            ) : null}

            {trailer.speed !== null && trailer.drivingStatus === 1 && (
              <Typography
                variant="caption"
                sx={{ fontSize: "0.65rem", color: "text.secondary" }}
              >
                {Math.round(trailer.speed * 0.621371)} mph
              </Typography>
            )}

            {trailer.batteryLevel !== null && (
              <Tooltip title={`Battery: ${trailer.batteryLevel}%`} placement="right">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <BatteryIcon level={trailer.batteryLevel} />
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.6rem", color: "text.secondary" }}
                  >
                    {trailer.batteryLevel}%
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

const TrailerSkeletonRow: React.FC = () => (
  <ListItem disablePadding>
    <Box sx={{ width: "100%", px: 2, py: 0.75 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="text" width="60%" height={18} />
        </Box>
        <Skeleton variant="text" width="45%" height={16} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Skeleton variant="rounded" width={54} height={18} />
          <Skeleton variant="text" width={36} height={16} />
          <Skeleton variant="rounded" width={42} height={16} />
        </Box>
      </Box>
    </Box>
  </ListItem>
);

export const TrailerList: React.FC<TrailerListProps> = ({
  trailers,
  selectedTrailerId,
  onTrailerSelect,
  isLoading = false,
  isAnyTrackEnabled = false,
  sentinelRef,
  listRef,
  isLoadingMore = false,
  hasMore = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <List
        ref={listRef}
        aria-label="Loading trailers"
        sx={{
          flex: 1,
          overflow: "hidden",
          bgcolor:
            theme.palette.mode === "light"
              ? colors.light.background.paper
              : colors.dark.background.paper,
        }}
      >
        {Array.from({ length: TRAILER_SKELETON_COUNT }).map((_, index) => (
          <TrailerSkeletonRow key={index} />
        ))}
      </List>
    );
  }

  if (!isAnyTrackEnabled) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 2,
          color: "text.secondary",
        }}
      >
        <InfoIcon sx={{ fontSize: "1.5rem", opacity: 0.5 }} />
        <Typography variant="caption" align="center" sx={{ fontSize: "0.7rem", opacity: 0.7 }}>
          Enable AnyTrack in map settings to view trailers
        </Typography>
      </Box>
    );
  }

  if (trailers.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
          No trailers found
        </Typography>
      </Box>
    );
  }

  return (
    <List
      ref={listRef}
      sx={{
        flex: 1,
        overflow: "auto",
        bgcolor:
          theme.palette.mode === "light"
            ? colors.light.background.paper
            : colors.dark.background.paper,
      }}
    >
      {trailers.map((trailer) => (
        <TrailerRow
          key={trailer.deviceId}
          trailer={trailer}
          isSelected={trailer.deviceId === selectedTrailerId}
          onSelect={() => onTrailerSelect(trailer.deviceId)}
        />
      ))}
      {(hasMore || isLoadingMore) && (
        <ListItem
          ref={sentinelRef}
          sx={{
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1,
          }}
        >
          <CircularProgress
            size={14}
            sx={{ visibility: isLoadingMore ? "visible" : "hidden" }}
          />
        </ListItem>
      )}
    </List>
  );
};
